/**
 * SuggestedPrompts — web port of `apps/WunderGuide/components/SuggestedPrompts.tsx`
 * + `apps/WunderGuide/common/CardCarousel.tsx`.
 *
 * Mirrors RN's CardCarousel "merry-go-round":
 *   • current slide   → translateX 0,   scale 1.00, opacity 1.0
 *   • prev / next     → translateX ±0.92×W, scale 0.88, opacity 0.4
 *   • cards beyond ±1 hidden
 *   • auto-advance every `autoPlayInterval` ms (default 12 000)
 *   • `transitionDuration` ms slide animation
 *
 * Identical config knobs as RN (read from useSuggestedPrompts globalConfig).
 * Manual swipe is supported via touch + a thin pagination dot row.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSuggestedPrompts } from '../hooks/useSuggestedPrompts';
import './SuggestedPrompts.css';

interface SuggestedPromptsProps {
  sessionId: string | null;
  enabled?: boolean;
  language?: string;
  destination?: string;
  timeOfDay?: 'morning' | 'afternoon' | 'evening' | 'night';
  onSelect?: (text: string) => void;
  fallback?: string[];
}

const NEIGHBOUR_SCALE = 0.88;
const NEIGHBOUR_OPACITY = 0.4;
const TRANSLATE_MULTIPLIER = 0.92;

const SuggestedPrompts: React.FC<SuggestedPromptsProps> = ({
  sessionId,
  enabled = true,
  language,
  destination,
  timeOfDay,
  onSelect,
  fallback = [],
}) => {
  const {
    prompts: apiPrompts,
    headerText,
    globalConfig,
  } = useSuggestedPrompts({
    sessionId,
    enabled,
    language,
    destination,
    timeOfDay,
  });

  const items = useMemo(
    () =>
      apiPrompts.length > 0
        ? apiPrompts.map(p => ({ id: p.promptId ?? p.text, text: p.text }))
        : fallback.map((text, i) => ({ id: `fallback-${i}`, text })),
    [apiPrompts, fallback],
  );

  const autoPlayInterval =
    globalConfig?.timing?.defaultAutoPlayInterval ?? 5_000;
  const transitionDuration =
    globalConfig?.timing?.defaultTransitionDuration ?? 800;
  const enableLoop = globalConfig?.timing?.enableLoop !== false;

  const [index, setIndex] = useState(0);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [stageWidth, setStageWidth] = useState(0);
  const pausedRef = useRef(false);
  const dragStartRef = useRef<{ x: number; t: number } | null>(null);

  // Latest values held in refs so the interval below can read them
  // without restarting every time props/state change. Restarting on every
  // render was causing the ticker to be torn down before it ever fired.
  const itemsLenRef = useRef(items.length);
  const enableLoopRef = useRef(enableLoop);
  itemsLenRef.current = items.length;
  enableLoopRef.current = enableLoop;

  // Measure stage so neighbours translate by the actual rendered width.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        setStageWidth(Math.round(e.contentRect.width));
      }
    });
    ro.observe(el);
    setStageWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Keep index in range when items change.
  useEffect(() => {
    if (index >= items.length) setIndex(0);
  }, [items.length, index]);

  // Auto-advance — single interval lifetime, only restarts when the
  // tick rate or enabled-state changes (NOT when items array changes).
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      if (pausedRef.current) return;
      if (itemsLenRef.current <= 1) return;
      setIndex(i => {
        const next = i + 1;
        if (next >= itemsLenRef.current) return enableLoopRef.current ? 0 : i;
        return next;
      });
    }, autoPlayInterval);
    return () => window.clearInterval(id);
  }, [enabled, autoPlayInterval]);

  const goTo = useCallback(
    (next: number) => {
      if (items.length === 0) return;
      const wrapped = enableLoop
        ? ((next % items.length) + items.length) % items.length
        : Math.max(0, Math.min(items.length - 1, next));
      setIndex(wrapped);
    },
    [items.length, enableLoop],
  );

  // Touch / mouse drag → step ±1 on threshold.
  const onPointerDown = (e: React.PointerEvent) => {
    pausedRef.current = true;
    dragStartRef.current = { x: e.clientX, t: Date.now() };
  };
  const onPointerUp = (e: React.PointerEvent) => {
    pausedRef.current = false;
    const start = dragStartRef.current;
    dragStartRef.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dt = Date.now() - start.t;
    if (Math.abs(dx) > 40 || (Math.abs(dx) > 16 && dt < 250)) {
      goTo(index + (dx < 0 ? 1 : -1));
    }
  };

  if (items.length === 0) return null;

  // Compute relative offset from current for each item; when looping we choose
  // the shortest path so wrap-around still slides smoothly.
  const relativeOffset = (i: number): number => {
    if (!enableLoop) return i - index;
    const n = items.length;
    let diff = i - index;
    if (diff > n / 2) diff -= n;
    if (diff < -n / 2) diff += n;
    return diff;
  };

  return (
    <div
      className="wg-prompts"
      onMouseEnter={() => (pausedRef.current = true)}
      onMouseLeave={() => (pausedRef.current = false)}
    >
      {(headerText || 'Try asking…') && (
        <div className="wg-prompts__header">{headerText || 'Try asking…'}</div>
      )}

      <div
        ref={stageRef}
        className="wg-prompts__stage"
        style={
          {
            ['--wg-prompts-fade' as any]: `${transitionDuration}ms`,
          } as React.CSSProperties
        }
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => (pausedRef.current = false)}
      >
        {items.map((item, i) => {
          const offset = relativeOffset(i);
          // Hide cards beyond ±1 so they don't render under the active pill.
          const visible = Math.abs(offset) <= 1;
          const tx = offset * stageWidth * TRANSLATE_MULTIPLIER;
          const scale = offset === 0 ? 1 : NEIGHBOUR_SCALE;
          const opacity = offset === 0 ? 1 : NEIGHBOUR_OPACITY;
          const z = offset === 0 ? 10 : 1;
          // Match RN: prompts are display-only inspiration unless the
          // caller explicitly wires `onSelect`. No onSelect → render a
          // non-interactive span (no button, no cursor, no a11y role).
          const interactive = typeof onSelect === 'function';
          const baseStyle: React.CSSProperties = {
            transform: `translate(-50%, -50%) translateX(${tx}px) scale(${scale})`,
            opacity: visible ? opacity : 0,
            zIndex: z,
            pointerEvents: interactive && offset === 0 ? 'auto' : 'none',
            transitionDuration: `${transitionDuration}ms`,
            cursor: interactive ? 'pointer' : 'default',
          };
          const className = `wg-prompts__pill${offset === 0 ? ' is-active' : ''}${interactive ? '' : ' is-inert'}`;
          if (!interactive) {
            return (
              <span
                key={item.id}
                className={className}
                aria-hidden={offset !== 0}
                style={baseStyle}
              >
                {item.text}
              </span>
            );
          }
          return (
            <button
              key={item.id}
              type="button"
              className={className}
              onClick={() => offset === 0 && onSelect?.(item.text)}
              tabIndex={offset === 0 ? 0 : -1}
              aria-hidden={offset !== 0}
              style={baseStyle}
            >
              {item.text}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SuggestedPrompts;
